
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserMenu } from "@/components/ui/UserMenu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { LogIn, Brain, Menu, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const Navbar = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const isMobile = useIsMobile();

  // Update active tab based on current path
  useEffect(() => {
    const path = location.pathname;
    if (path === "/") {
      setActiveTab("dashboard");
    } else if (path.includes("/clients")) {
      setActiveTab("clients");
    } else if (path.includes("/sessions")) {
      setActiveTab("sessions");
    } else if (path.includes("/consultation")) {
      setActiveTab("consultation");
    } else if (path.includes("/dictation")) {
      setActiveTab("dictation");
    }
  }, [location.pathname]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navigationItems = [
    { to: "/", label: "Dashboard", key: "dashboard" },
    { to: "/consultation", label: "Consultation", key: "consultation" },
    { to: "/dictation", label: "Dictation", key: "dictation" },
    { to: "/clients", label: "Clients", key: "clients" },
    { to: "/sessions", label: "Sessions", key: "sessions" },
  ];

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-10 rounded-lg w-full">
      <div className="flex items-center justify-between mobile-container py-3 sm:py-4">
        {/* Logo - Left Side */}
        <div className="flex items-center">
          <a href="/" className="flex items-center" aria-label="Home">
            <img
              src="/psybyteslogo.svg"
              alt="PsyBytes Logo"
              className="h-7 sm:h-8 mr-2"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                if (img.src.includes('/psybyteslogo.svg')) {
                  img.src = '/public/psybyteslogo.svg';
                } else if (img.src.includes('/public/psybyteslogo.svg')) {
                  img.src = '/assets/psybyteslogo.svg';
                } else {
                  img.style.display = 'none';
                  const iconFallback = img.parentElement?.querySelector('.logo-fallback');
                  if (iconFallback) {
                    (iconFallback as HTMLElement).style.display = 'flex';
                  }
                }
              }}
            />
            <div className="logo-fallback h-7 sm:h-8 w-7 sm:w-8 mr-2 hidden items-center justify-center bg-blue-100 rounded-lg">
              <Brain className="h-4 sm:h-5 w-4 sm:w-5 text-blue-600" />
            </div>
            <span className="text-xl sm:text-2xl font-medium text-black font-serif">PsyBytes</span>
          </a>
        </div>

        {/* Navigation - Center (Desktop) */}
        {isAuthenticated && !isMobile && (
          <nav className="hidden md:flex items-center gap-4 lg:gap-6 absolute left-1/2 transform -translate-x-1/2">
            {navigationItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={`px-3 py-2 rounded-lg transition-all duration-200 mobile-touch-target ${
                  activeTab === item.key
                    ? "text-white font-medium bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg transform scale-105"
                    : "text-gray-600 hover:text-blue-600 hover:bg-blue-50 hover:shadow-md hover:scale-102"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}

        {/* Right Side - User Menu and Mobile Menu Button */}
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Mobile Menu Button */}
          {isAuthenticated && isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden mobile-touch-target p-2"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          )}

          {/* User Profile */}
          {isAuthenticated ? (
            <UserMenu />
          ) : (
            <Link to="/login">
              <Button variant="outline" size="sm" className="mobile-touch-target">
                <LogIn className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Sign In</span>
                <span className="sm:hidden">Sign In</span>
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isAuthenticated && isMobile && mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white">
          <nav className="mobile-container py-4 space-y-1">
            {navigationItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={`block px-3 py-3 rounded-lg transition-all duration-200 mobile-touch-target ${
                  activeTab === item.key
                    ? "text-white font-medium bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg"
                    : "text-gray-600 hover:text-blue-600 hover:bg-blue-50 hover:shadow-md"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
